import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  GripVertical, Save, Eye, BarChart3, PieChart, LineChart, 
  Activity, Clock, Users, TrendingUp, FileText, Plus
} from 'lucide-react';
import { DEFAULT_WIDGETS, ReportWidget, ReportFilters, useReportTemplates } from '@/hooks/useReportTemplates';
import { toast } from 'sonner';

const WIDGET_ICONS: Record<string, React.ReactNode> = {
  executive_summary: <FileText className="h-4 w-4" />,
  kpi: <Activity className="h-4 w-4" />,
  sla: <Clock className="h-4 w-4" />,
  productivity: <TrendingUp className="h-4 w-4" />,
  team: <Users className="h-4 w-4" />,
  status_pie: <PieChart className="h-4 w-4" />,
  type_bar: <BarChart3 className="h-4 w-4" />,
  timeline: <LineChart className="h-4 w-4" />,
};

interface ReportBuilderProps {
  onPreview: (widgets: ReportWidget[]) => void;
}

export const ReportBuilder = ({ onPreview }: ReportBuilderProps) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [widgets, setWidgets] = useState<ReportWidget[]>([...DEFAULT_WIDGETS]);
  const { createTemplate } = useReportTemplates();

  const toggleWidget = (id: string) => {
    setWidgets(prev =>
      prev.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w)
    );
  };

  const moveWidget = (id: string, direction: 'up' | 'down') => {
    setWidgets(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex(w => w.id === id);
      if (direction === 'up' && idx > 0) {
        const temp = sorted[idx].order;
        sorted[idx].order = sorted[idx - 1].order;
        sorted[idx - 1].order = temp;
      } else if (direction === 'down' && idx < sorted.length - 1) {
        const temp = sorted[idx].order;
        sorted[idx].order = sorted[idx + 1].order;
        sorted[idx + 1].order = temp;
      }
      return sorted;
    });
  };

  const handleSave = () => {
    if (!nome.trim()) {
      toast.error('Informe um nome para o template');
      return;
    }
    createTemplate.mutate({
      nome: nome.trim(),
      descricao: descricao.trim() || undefined,
      widgets,
      filtros: {} as ReportFilters,
    });
  };

  const enabledCount = widgets.filter(w => w.enabled).length;
  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Criar Relatório Customizado
          </CardTitle>
          <CardDescription>
            Selecione os widgets, organize a ordem e salve como template reutilizável
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Nome do Template</Label>
              <Input
                id="template-name"
                placeholder="Ex: Relatório Mensal Executivo"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-desc">Descrição (opcional)</Label>
              <Input
                id="template-desc"
                placeholder="Breve descrição do relatório"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Widget Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Widgets do Relatório</CardTitle>
              <CardDescription>
                {enabledCount} de {widgets.length} widgets ativos
              </CardDescription>
            </div>
            <Badge variant="secondary">{enabledCount} ativos</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sortedWidgets.map((widget, idx) => (
              <div
                key={widget.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  widget.enabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveWidget(widget.id, 'up')}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                    >
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor"><path d="M6 0L12 8H0L6 0Z"/></svg>
                    </button>
                    <button
                      onClick={() => moveWidget(widget.id, 'down')}
                      disabled={idx === sortedWidgets.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                    >
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor"><path d="M6 8L0 0H12L6 8Z"/></svg>
                    </button>
                  </div>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <div className="flex items-center gap-2">
                    {WIDGET_ICONS[widget.type]}
                    <span className={`text-sm font-medium ${widget.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {widget.label}
                    </span>
                  </div>
                </div>
                <Switch
                  checked={widget.enabled}
                  onCheckedChange={() => toggleWidget(widget.id)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => onPreview(widgets.filter(w => w.enabled).sort((a, b) => a.order - b.order))}
        >
          <Eye className="h-4 w-4" />
          Pré-visualizar
        </Button>
        <Button className="gap-2" onClick={handleSave} disabled={createTemplate.isPending}>
          <Save className="h-4 w-4" />
          Salvar Template
        </Button>
      </div>
    </div>
  );
};
